// AutoApproveAllButton — shared 1-click "trust the agent" affordance for
// G1, G2, G3, G5 (plan §P1.12 / UI Workflow Consistency Audit §6X.4).
//
// G4 has its own bespoke Auto-Approve because it needs to skip rewrites
// the classifier flagged as ``unresolved`` / ``needs_review``. The four
// other gates are simpler: every still-``pending`` decision flips to
// ``approved``; existing edits / rejections are preserved.
//
// The button is intentionally rendered inline next to each gate's
// existing primary CTA. We surface a count chip on the right so the
// architect knows how many decisions are about to be auto-approved.

import { CheckCheck } from 'lucide-react'

import { toast } from '@/hooks/use-toast'
import type { GateDecisionRecord } from '@/types'

export interface AutoApproveAllButtonProps {
  /** Current decisions array; we only flip rows where ``status === 'pending'``. */
  decisions: GateDecisionRecord[]
  /** Setter that the gate already uses to push edits back into the draft store. */
  setDecisions: (next: GateDecisionRecord[]) => void
  /** Used in the toast description so we don't have to hard-code "G2". */
  gateName: string
  /**
   * Optional predicate that lets a caller exclude certain decision ids
   * from the bulk approve (e.g. G5's mis-fit rows that genuinely need
   * a manual triage). Returns ``true`` to skip the row.
   */
  skip?: (decision: GateDecisionRecord) => boolean
  /** Disable while a parent mutation is in flight. */
  disabled?: boolean
}

export function AutoApproveAllButton({
  decisions,
  setDecisions,
  gateName,
  skip,
  disabled,
}: AutoApproveAllButtonProps) {
  const pending = decisions.filter(
    (d) => d.status === 'pending' && !(skip?.(d) ?? false),
  )
  const pendingCount = pending.length

  const handleClick = () => {
    if (pendingCount === 0) {
      toast({
        title: 'Nothing to approve',
        description: 'All decisions already have a status.',
      })
      return
    }
    const next = decisions.map((d) => {
      if (d.status !== 'pending') return d
      if (skip?.(d)) return d
      return { ...d, status: 'approved' as const }
    })
    setDecisions(next)
    toast({
      variant: 'success',
      title: `Auto-approved ${pendingCount} ${
        pendingCount === 1 ? 'decision' : 'decisions'
      }`,
      description: `Click "Approve & continue" to submit ${gateName}.`,
    })
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || pendingCount === 0}
      title={
        pendingCount === 0
          ? 'No pending decisions to auto-approve'
          : `Mark all ${pendingCount} pending ${gateName} decisions as approved`
      }
      className="inline-flex items-center gap-2 rounded-lg border border-indigo-700/60 bg-indigo-950/30 px-4 py-2 text-sm font-semibold text-indigo-200 hover:bg-indigo-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <CheckCheck className="h-4 w-4" />
      Auto Approve
      {pendingCount > 0 && (
        <span className="alm-chip alm-chip-info text-[10px] font-mono ml-1">
          {pendingCount}
        </span>
      )}
    </button>
  )
}
